/** Yiriba UI strings (en/it/fr). Shared with pfh-mobile, which merges them into its own i18next. */
export const tsumiEn = {
  navGroups: { portfolio: 'Portfolio', markets: 'Markets', tools: 'Tools', reports: 'Reports' },
  accountMenu: {
    label: 'Account menu',
    profile: 'Profile',
    dataStays: 'Your data is yours — cloud AI only if you opt in',
    appearance: 'Appearance',
    language: 'Language',
    themeDark: 'Dark',
    themeLight: 'Light',
    themeSystem: 'System',
  },
  jeli: {
    emptyTitle: 'Ask Jeli about your investments',
    emptyBody: 'Jeli can explain your positions, markets and simulations. It sees the page you are on.',
    inputLabel: 'Message for Jeli',
    placeholder: 'Ask a question…',
    send: 'Send',
    newChat: 'Start a new chat',
  },
  switcher: { kelen: 'Net worth, goals, simulation', yiriba: 'ETFs, crypto, markets', naforo: 'Bank accounts and budgets' },
  yiribaShell: { dataSources: 'Data sources & licences' },
  nav: {
    lab: 'Jeli Lab',
    asset: 'Asset',
    home: 'Home',
    portfolio: 'Portfolio',
    etf: 'ETF Explorer',
    crypto: 'Crypto Explorer',
    analysis: 'Market Analysis',
    backtest: 'Backtest',
    screener: 'Screener',
    sentiment: 'Sentiment',
    manualAssets: 'Manual Assets',
    import: 'Import',
    importLog: 'Import Log',
    comparator: 'Comparator',
    simulations: 'Simulations',
    tax: 'Tax',
    export: 'Export',
    settings: 'Settings',
    help: 'Help',
    legal: 'Legal',
    security: 'Security',
    about: 'About',
  },
  portfolio: {
    title: 'Portfolio',
    totalValue: 'Total Value',
    pnl: 'P&L',
    positions: 'Positions',
    empty: 'No positions yet — add your first asset.',
    subtitle: 'ETF and crypto positions, valued in euro.',
    totalPortfolio: 'Total portfolio',
    totalReturn: 'total return',
    etf: 'ETF',
    crypto: 'Crypto',
    capitalInvested: 'Capital invested',
    growth: 'Growth',
    valueOverTime: 'Value over time',
    noHistory: 'No history available yet.',
    valueLabel: 'Value',
    allocation: 'Allocation by asset class',
    colAsset: 'Asset',
    colValue: 'Value',
    colReturn: 'Total return',
    colType: 'Type',
    offline: 'Yiriba backend offline — showing cached data if available.',
    positionsError: 'Could not load your positions. The total above may not reflect a complete picture — please try again in a moment.',
    loadError: 'Could not load portfolio. Please try again in a moment.',
  },
  common: {
    loading: 'Loading\u2026',
    error: 'Something went wrong.',
    retry: 'Retry',
    noData: 'No data available.',
    disclaimer: 'Informational only \u2014 not financial advice.',
    equityDataLicenseNote: 'Equity market data is provided under a free-tier, personal/non-commercial license. A paid commercial license will be required before any commercial launch of this app.',
  },
  notFound: {
    title: 'Page not found',
    body: 'The page you are looking for does not exist or has moved.',
    home: 'Back to Portfolio',
  },
  a11y: {
    assetClass: 'Asset class',
    screenType: 'Screen type',
    direction: 'Direction',
    rsiCondition: 'RSI condition',
    universe: 'Universe',
    symbol: 'Symbol',
    etfSymbol: 'ETF symbol',
    equitySymbol: 'Equity symbol',
    addSymbol: 'Add symbol',
    assetNotFoundTitle: 'Asset not found',
    assetNotFoundBody: 'No asset matches this identifier. Check the symbol and try again.',
    askJeli: 'Ask Jeli',
  },
  markets: {
    title: 'Market Analysis',
    crypto: 'Crypto',
    equity: 'Equity',
    etf: 'ETF',
    bond: 'Bond',
    addSymbolPlaceholder: 'Add symbol from catalog\u2026',
    equitySymbolPlaceholder: 'Symbol e.g. AAPL',
    add: 'Add',
    analyze: 'Analyze',
    analyzing: 'Analyzing\u2026',
    compare: 'Compare',
    selectHint: 'Select 1 symbol to analyze, or 2\u2013{{max}} to compare side by side.',
    technicalIndicators: 'Technical Indicators',
    multiAssetCompare: 'Multi-Asset Compare',
    emptyAnalyze: 'Add a symbol and click Analyze.',
    correlation: 'Correlation',
    compareHint: 'Select 2\u2013{{max}} symbols and click Compare to see pairwise correlation.',
    rating: 'Rating',
    removeSymbol: 'Remove {{symbol}}',
  },
  backtest: {
    title: 'Backtesting',
    expertRequired: 'Backtesting is an Expert feature. Switch to an Expert account to unlock it.',
    run: 'Run Backtest',
    running: 'Running\u2026',
    strategyBacktest: 'Strategy Backtest',
    totalReturn: 'Total Return',
    vsBuyHold: 'vs Buy & Hold',
    sharpe: 'Sharpe',
    maxDrawdown: 'Max Drawdown',
    winRate: 'Win Rate',
    trades: 'Trades',
    selectStrategyHint: 'Select a strategy and run backtest.',
    compareStrategiesTitle: 'Compare Strategies & Walk-Forward',
    compareStrategies: 'Compare Strategies',
    runCompare: 'Run Compare',
    comparing: 'Comparing\u2026',
    strategy: 'Strategy',
    maxDD: 'Max DD',
    best: 'Best',
    compareHint: 'Select at least 2 strategies above, then run compare to rank by Sharpe.',
    walkForward: 'Walk-Forward Analysis',
    runWalkForward: 'Run Walk-Forward',
    insufficientHistory: 'Not enough history for a walk-forward split on this symbol/lookback. Try a longer lookback or a different symbol.',
    inSampleReturn: 'In-Sample Return',
    inSampleSharpe: 'In-Sample Sharpe',
    oosReturn: 'OOS Return',
    oosSharpe: 'OOS Sharpe',
    walkForwardHint: 'Overfitting detection via in-sample / out-of-sample split.',
    equitySymbolPlaceholder: 'Symbol e.g. AAPL',
  },
  screener: {
    title: 'Market Screener',
    subtitle: 'Find ETFs, equities and crypto that match your criteria. Results are informational, not advice.',
    topN: 'Number of coins',
    errorTitle: 'The scan did not complete',
    errorHint: 'Check your connection and try again.',
    locked: {
      description: 'Scan the market for movers, unusual volume, RSI extremes and 52-week breakouts.',
      benefit1: 'One-click presets for common strategies',
      benefit2: 'Your own watchlist or the top coins as the universe',
      benefit3: 'Send any result straight to Market Analysis',
    },
    empty: {
      title: 'No results match these filters',
      message: 'Try a wider range, another screen or a different universe.',
      reset: 'Reset filters',
    },
    scan: 'Scan',
    scanning: 'Scanning\u2026',
    topMovers: 'Top Movers',
    volumeScanner: 'Volume Scanner',
    ratingFilter: 'Rating Filter',
    scannedSymbols: 'Scanned {{count}} symbols \u00b7 {{results}} results',
    symbol: 'Symbol',
    price: 'Price',
    change: 'Change',
    volRatio: 'Vol Ratio',
    rsi: 'RSI',
    ratingCol: 'Rating',
    presetsLabel: 'One-click presets',
    preset: {
      momentum: 'Momentum',
      rsi_oversold: 'RSI Oversold',
      breakout_52w: '52W Breakout',
      high_volume: 'High Volume',
    },
    gainers: 'Gainers',
    losers: 'Losers',
    minVolRatio: 'Min volume ratio',
    ratingLabelFilter: 'Rating label',
    minScore: 'Min score',
    rsiThreshold: 'RSI threshold',
    oversold: 'Oversold',
    overbought: 'Overbought',
    breakoutThreshold: 'Breakout proximity',
    savePresetPlaceholder: 'Preset name\u2026',
    savePreset: 'Save preset',
    savedPresets: 'Saved presets',
    deletePreset: 'Delete {{name}}',
    universeDefault: 'Default universe',
    universeWatchlist: 'My watchlist',
    universeTopCrypto: 'Top {{n}} crypto',
    universeCatalog: 'Equity catalog (US+EU)',
    watchlistPlaceholder: 'Add symbol\u2026',
    addSymbol: 'Add',
    expertRequired: 'The Market Screener is an Expert feature. Upgrade to an Expert account to unlock it.',
    selectRow: 'Select {{symbol}}',
    selectHint: 'Select up to {{max}} rows to compare, or click a row to view its analysis.',
    compareSelected: 'Compare selected ({{count}})',
    viewDetail: 'View analysis',
  },
  sentiment: {
    title: 'Sentiment & News',
    fetch: 'Fetch',
    loading: 'Loading\u2026',
    sentimentAnalysis: 'Sentiment Analysis',
    score: 'Score',
    articles: 'Articles',
    perSourceBreakdown: 'Per-source breakdown',
    source: 'Source',
    label: 'Label',
    priceSentimentDivergence: 'Price/Sentiment Divergence',
    priceChange: 'Price change',
    sentimentScore: 'Sentiment score',
    newsHeadlines: 'News Headlines',
    selectSymbolHint: 'Select a symbol and click Fetch.',
    equitySymbolPlaceholder: 'Symbol e.g. AAPL',
  },
  importPage: {
    title: 'Import',
    subtitle: 'Upload a CSV or Excel export. AI maps the columns; you edit and confirm before anything is imported.',
    step1: '1 Upload',
    step2: '2 Edit and confirm',
    step3: '3 Done',
  },
  settingsPage: {
    accountsTitle: 'Investment accounts',
    accountsDescription: 'Connect Binance, DeGiro, or a public wallet address to sync your portfolio.',
  },
  manualAssets: {
    title: 'Manual Assets',
    subtitle: 'Assets you track by hand, such as property, cash or private holdings.',
  },
  importLog: {
    title: 'Import Log',
    subtitle: 'Audit trail of every CSV/Excel file imported into Yiriba.',
    filename: 'File',
    destination: 'Destination',
    rows: 'Rows',
    imported: 'Imported',
    skipped: 'Skipped',
    errors: 'Errors',
    date: 'Date',
    empty: 'No imports yet.',
    error: 'Could not load the import log.',
    loading: 'Loading\u2026',
  },
  explorer: {
    etfTitle: 'ETF Explorer',
    etfDescription: 'Browse the ETF catalog or view your positions',
    cryptoTitle: 'Crypto Explorer',
    cryptoDescription: 'Browse the crypto catalog or view your positions',
    catalog: 'Catalog',
    myEtf: 'My ETF',
    browse: 'Browse',
    myCrypto: 'My Crypto',
    search: 'Search',
    etfSearchPlaceholder: 'Name or ISIN',
    cryptoSearchPlaceholder: 'Name or symbol',
    assetClass: 'Asset class',
    domicile: 'Domicile',
    category: 'Category',
    ranked: 'Ranked',
    all: 'All',
    top: 'Top {{n}}',
    peaEligibleOnly: 'PEA eligible only',
    isin: 'ISIN',
    name: 'Name',
    ter: 'TER',
    noEtfMatch: 'No ETFs match these filters.',
    noCryptoMatch: 'No cryptocurrencies match these filters.',
    previous: 'Previous',
    next: 'Next',
    etfCatalogError: 'Could not load ETF catalog. Please try again in a moment.',
    etfPositionsError: 'Could not load ETF positions. Please try again in a moment.',
    cryptoCatalogError: 'Could not load crypto catalog. Please try again in a moment.',
    cryptoPositionsError: 'Could not load crypto positions. Please try again in a moment.',
    qty: 'Qty',
    price: 'Price',
    value: 'Value',
    totalReturn: 'Total return',
    noEtfPositions: 'No ETF positions found.',
    noCryptoPositions: 'No crypto positions found.',
    rank: 'Rank',
    symbol: 'Symbol',
    marketCap: 'Market Cap',
    ath: 'ATH',
    atl: 'ATL',
    refresh: 'Refresh prices',
    refreshing: 'Refreshing\u2026',
    refreshSuccess: 'Prices refreshed.',
    refreshError: 'Refresh failed \u2014 try again later.',
  },
  home: {
    title: 'Yiriba',
    subtitle: 'Personal ETF & crypto portfolio tracker, analyser, and simulator.',
    portfolioCard: 'Portfolio',
    portfolioDesc: 'Aggregate P&L across DeGiro, Binance, and Boursorama.',
    etfCard: 'ETF Explorer',
    etfDesc: 'Browse and filter the full ETF catalog with PEA badges.',
    cryptoCard: 'Crypto Explorer',
    cryptoDesc: 'Live prices, indicators, and your Binance positions.',
    analysisCard: 'Market Analysis',
    analysisDesc: 'Multi-timeframe technicals, ratings, and sentiment.',
    taxCard: 'Tax',
    taxDesc: 'Indicative Italian and French capital-gains tax estimates.',
    exportCard: 'Export',
    exportDesc: 'Download your portfolio and tax data as Excel or CSV.',
    open: 'Open',
  },
  tax: {
    title: 'Tax',
    staticDisclaimer: 'Automatically generated indicative estimate. Always verify with a qualified tax advisor. No guarantee of correctness or regulatory up-to-dateness.',
    italyTab: 'Italy',
    franceTab: 'France',
    gainsTab: 'Realized gains',
    methodFifo: 'FIFO (first in, first out)',
    methodAverage: 'Average cost',
    acquired: 'Acquired',
    sold: 'Sold',
    proceeds: 'Proceeds',
    costBasis: 'Cost basis',
    daysHeld: 'Days held',
    exportCsv: 'Download CSV',
    pooled: 'Pooled',
    noRealized: 'No sales found for this year.',
    unmatchedWarning: 'Some sales exceed your recorded purchases; the excess uses a cost basis of 0.',
    year: 'Year',
    method: 'Matching method',
    source: 'Source',
    allSources: 'All (DeGiro + Binance)',
    degiro: 'DeGiro',
    binance: 'Binance',
    includeManualHoldings: 'Include manually-added holdings',
    peaOpenDate: 'PEA opening date',
    fetch: 'Calculate',
    fetching: 'Calculating\u2026',
    etfGains: 'ETF capital gains',
    cryptoGains: 'Crypto capital gains',
    netGain: 'Net gain',
    taxDue: 'Tax due',
    grossGains: 'Gross gains',
    grossLosses: 'Gross losses',
    ivafe: 'IVAFE (0.2% foreign assets)',
    foreignValue: 'Foreign asset value',
    quadroRw: 'Quadro RW',
    requiresRw: 'Quadro RW required',
    yes: 'Yes',
    no: 'No',
    lossCarryforward: 'Loss carryforward',
    offsetApplied: 'Offset applied',
    remainingGain: 'Remaining taxable gain',
    maxYears: 'Carryforward window: {{years}} years',
    trades: 'Trades',
    symbol: 'Symbol',
    quantity: 'Quantity',
    sellPrice: 'Sell price',
    avgCost: 'Avg. cost',
    gain: 'Gain/Loss',
    date: 'Date',
    noTrades: 'No taxable trades found for this year.',
    peaStatus: 'PEA status',
    mature: 'Mature (\u22655y) \u2014 PS only',
    notMature: 'Not mature (<5y) \u2014 full PFU',
    holdingYears: '{{years}} years held',
    psTax: 'Social contributions (PS 17.2%)',
    irTax: 'Income tax (IR)',
    cto: 'CTO (PFU 30%)',
    pea: 'PEA',
    form2042: 'Form 2042 fields',
    form2074Title: 'Form 2074',
    form2074Required: 'Required \u2014 {{reason}}',
    form2074NotRequired: 'Not required',
    selectYearHint: 'Select a year and click Calculate.',
    error: 'Could not calculate tax estimate.',
  },
  export: {
    title: 'Export',
    excelReportTitle: 'Full Excel Report',
    excelReportDesc: 'Multi-sheet workbook: summary, positions per broker, transaction history, performance, and fiscal estimates (IT/FR).',
    year: 'Year',
    source: 'Broker',
    allSources: 'All brokers',
    degiro: 'DeGiro',
    binance: 'Binance',
    boursorama: 'Boursorama',
    downloadExcel: 'Download Excel Report',
    downloading: 'Downloading\u2026',
    positionsTitle: 'Portfolio Positions',
    positionsDesc: 'Current positions only, flat list \u2014 no fiscal sheets.',
    format: 'Format',
    downloadPositions: 'Download Positions',
    downloadSuccess: 'Download started.',
    downloadError: 'Download failed.',
  },
  comparator: {
    title: 'Comparator',
    expertRequired: 'The Comparator is an Expert feature. Switch to an Expert account to unlock it.',
    subtitle: 'Compare up to 5 assets side by side',
    selectAssets: 'Select assets',
    addAsset: 'Add asset',
    maxAssetsReached: 'Maximum 5 assets',
    minAssetsRequired: 'Select at least 2 assets',
    performanceChart: 'Normalized performance (base 100)',
    statsTable: 'Statistics',
    correlationMatrix: 'Correlation matrix',
    overlapMatrix: 'Holdings overlap %',
    cagr: 'CAGR',
    volatility: 'Volatility',
    sharpe: 'Sharpe',
    maxDrawdown: 'Max Drawdown',
    loading: 'Comparing assets...',
    error: 'Unable to load comparison',
  },
  simulations: {
    title: 'Simulations',
    expertRequired: 'Simulations are an Expert feature. Switch to an Expert account to unlock them.',
    subtitle: 'Test how a purchase, a plan or a market scenario would change your portfolio.',
    tabImpact: 'Impact Analysis',
    tabMonteCarlo: 'Monte Carlo',
    tabPrePurchase: 'Pre-purchase stats',
    impactAsset: 'Asset (ISIN or symbol)',
    impactAmount: 'Amount (EUR)',
    runImpact: 'Simulate purchase',
    deltaSharpe: 'Δ Sharpe',
    deltaVolatility: 'Δ Volatility',
    newWeight: 'New weight %',
    correlation: 'Correlation',
    mcInitial: 'Initial amount (EUR)',
    mcMonthly: 'Monthly contribution (EUR)',
    mcYears: 'Years',
    mcReturn: 'Expected annual return',
    mcVolatility: 'Expected annual volatility',
    runMonteCarlo: 'Run simulation',
    probDouble: 'Probability of doubling',
    prePurchaseAsset: 'Asset (ISIN or symbol)',
    runPrePurchase: 'Analyze',
    overlapPercentage: 'Overlap %',
    valueAtRisk: 'VaR 95%',
    loading: 'Simulating...',
    error: 'The simulation could not be completed. Please try again.',
  },
};
export const tsumiIt = {
  navGroups: { portfolio: 'Portafoglio', markets: 'Mercati', tools: 'Strumenti', reports: 'Report' },
  accountMenu: {
    label: 'Menu account',
    profile: 'Profilo',
    dataStays: 'I tuoi dati sono tuoi — AI cloud solo se lo scegli',
    appearance: 'Aspetto',
    language: 'Lingua',
    themeDark: 'Scuro',
    themeLight: 'Chiaro',
    themeSystem: 'Sistema',
  },
  jeli: {
    emptyTitle: 'Chiedi a Jeli dei tuoi investimenti',
    emptyBody: 'Jeli può spiegarti posizioni, mercati e simulazioni. Vede la pagina in cui ti trovi.',
    inputLabel: 'Messaggio per Jeli',
    placeholder: 'Fai una domanda…',
    send: 'Invia',
    newChat: 'Nuova conversazione',
  },
  switcher: { kelen: 'Patrimonio, obiettivi, simulazione', yiriba: 'ETF, crypto, mercati', naforo: 'Conti bancari e budget' },
  yiribaShell: { dataSources: 'Fonti dati e licenze' },
  nav: {
    lab: 'Jeli Lab',
    asset: 'Asset',
    home: 'Home',
    portfolio: 'Portafoglio',
    etf: 'Esplora ETF',
    crypto: 'Esplora Crypto',
    analysis: 'Analisi di mercato',
    backtest: 'Backtesting',
    screener: 'Screener',
    sentiment: 'Sentiment',
    manualAssets: 'Asset Manuali',
    import: 'Importa',
    importLog: 'Registro import',
    comparator: 'Comparatore',
    simulations: 'Simulazioni',
    tax: 'Fiscale',
    export: 'Esporta',
    settings: 'Impostazioni',
    help: 'Aiuto',
    legal: 'Note legali',
    security: 'Sicurezza',
    about: 'Informazioni',
  },
  portfolio: {
    title: 'Portafoglio',
    totalValue: 'Valore totale',
    pnl: 'Profitto/Perdita',
    positions: 'Posizioni',
    empty: 'Nessuna posizione \u2014 aggiungi il tuo primo asset.',
    subtitle: 'Posizioni ETF e crypto, valutate in euro.',
    totalPortfolio: 'Portafoglio totale',
    totalReturn: 'rendimento totale',
    etf: 'ETF',
    crypto: 'Crypto',
    capitalInvested: 'Capitale investito',
    growth: 'Crescita',
    valueOverTime: 'Valore nel tempo',
    noHistory: 'Nessuno storico disponibile.',
    valueLabel: 'Valore',
    allocation: 'Allocazione per classe di attivo',
    colAsset: 'Attivo',
    colValue: 'Valore',
    colReturn: 'Rendimento totale',
    colType: 'Tipo',
    offline: 'Backend di Yiriba non raggiungibile — vengono mostrati i dati in cache, se disponibili.',
    positionsError: 'Impossibile caricare le posizioni. Il totale qui sopra potrebbe non essere completo — riprova tra un momento.',
    loadError: 'Impossibile caricare il portafoglio. Riprova tra un momento.',
  },
  common: {
    loading: 'Caricamento\u2026',
    error: 'Qualcosa \u00e8 andato storto.',
    retry: 'Riprova',
    noData: 'Nessun dato disponibile.',
    disclaimer: 'Solo a scopo informativo \u2014 non \u00e8 una consulenza finanziaria.',
    equityDataLicenseNote: 'I dati di mercato azionari sono forniti tramite una licenza free-tier per uso personale/non commerciale. Sar\u00e0 necessaria una licenza commerciale a pagamento prima di un eventuale lancio commerciale dell\u2019app.',
  },
  notFound: {
    title: 'Pagina non trovata',
    body: 'La pagina che cerchi non esiste o \u00e8 stata spostata.',
    home: 'Torna al Portafoglio',
  },
  a11y: {
    assetClass: 'Classe di attivit\u00e0',
    screenType: 'Tipo di screening',
    direction: 'Direzione',
    rsiCondition: 'Condizione RSI',
    universe: 'Universo',
    symbol: 'Simbolo',
    etfSymbol: 'Simbolo ETF',
    equitySymbol: 'Simbolo azione',
    addSymbol: 'Aggiungi simbolo',
    assetNotFoundTitle: 'Asset non trovato',
    assetNotFoundBody: 'Nessun asset corrisponde a questo identificativo. Controlla il simbolo e riprova.',
    askJeli: 'Chiedi a Jeli',
  },
  markets: {
    title: 'Analisi di mercato',
    crypto: 'Crypto',
    equity: 'Azionario',
    etf: 'ETF',
    bond: 'Obbligazioni',
    addSymbolPlaceholder: 'Aggiungi simbolo dal catalogo\u2026',
    equitySymbolPlaceholder: 'Simbolo es. AAPL',
    add: 'Aggiungi',
    analyze: 'Analizza',
    analyzing: 'Analisi in corso\u2026',
    compare: 'Confronta',
    selectHint: 'Seleziona 1 simbolo da analizzare, oppure 2\u2013{{max}} per confrontarli fianco a fianco.',
    technicalIndicators: 'Indicatori tecnici',
    multiAssetCompare: 'Confronto multi-asset',
    emptyAnalyze: 'Aggiungi un simbolo e clicca Analizza.',
    correlation: 'Correlazione',
    compareHint: 'Seleziona 2\u2013{{max}} simboli e clicca Confronta per vedere la correlazione a coppie.',
    rating: 'Rating',
    removeSymbol: 'Rimuovi {{symbol}}',
  },
  backtest: {
    title: 'Backtesting',
    expertRequired: 'Il backtesting \u00e8 una funzione Expert. Passa a un account Expert per sbloccarlo.',
    run: 'Esegui backtest',
    running: 'Esecuzione\u2026',
    strategyBacktest: 'Backtest strategia',
    totalReturn: 'Rendimento totale',
    vsBuyHold: 'vs Buy & Hold',
    sharpe: 'Sharpe',
    maxDrawdown: 'Max Drawdown',
    winRate: 'Win Rate',
    trades: 'Operazioni',
    selectStrategyHint: 'Seleziona una strategia ed esegui il backtest.',
    compareStrategiesTitle: 'Confronta strategie & Walk-Forward',
    compareStrategies: 'Confronta strategie',
    runCompare: 'Esegui confronto',
    comparing: 'Confronto in corso\u2026',
    strategy: 'Strategia',
    maxDD: 'Max DD',
    best: 'Migliore',
    compareHint: 'Seleziona almeno 2 strategie sopra, poi esegui il confronto per classificarle per Sharpe.',
    walkForward: 'Analisi Walk-Forward',
    runWalkForward: 'Esegui Walk-Forward',
    insufficientHistory: 'Storico insufficiente per uno split walk-forward su questo simbolo/periodo. Prova un periodo pi\u00f9 lungo o un simbolo diverso.',
    inSampleReturn: 'Rendimento in-sample',
    inSampleSharpe: 'Sharpe in-sample',
    oosReturn: 'Rendimento OOS',
    oosSharpe: 'Sharpe OOS',
    walkForwardHint: 'Rilevamento overfitting tramite split in-sample / out-of-sample.',
    equitySymbolPlaceholder: 'Simbolo es. AAPL',
  },
  screener: {
    title: 'Screener di mercato',
    subtitle: 'Trova ETF, azioni e crypto che rispondono ai tuoi criteri. I risultati sono informativi, non consigli.',
    topN: 'Numero di monete',
    errorTitle: 'La scansione non è stata completata',
    errorHint: 'Controlla la connessione e riprova.',
    locked: {
      description: 'Scansiona il mercato per movimenti, volumi anomali, estremi RSI e massimi a 52 settimane.',
      benefit1: 'Preset a un clic per le strategie più comuni',
      benefit2: 'La tua watchlist o le principali crypto come universo',
      benefit3: 'Invia qualsiasi risultato all\u2019Analisi di mercato',
    },
    empty: {
      title: 'Nessun risultato per questi filtri',
      message: 'Prova un intervallo più ampio, un altro screen o un universo diverso.',
      reset: 'Azzera i filtri',
    },
    scan: 'Scansiona',
    scanning: 'Scansione in corso\u2026',
    topMovers: 'Top Movers',
    volumeScanner: 'Scanner volumi',
    ratingFilter: 'Filtro rating',
    scannedSymbols: 'Scansionati {{count}} simboli \u00b7 {{results}} risultati',
    symbol: 'Simbolo',
    price: 'Prezzo',
    change: 'Variazione',
    volRatio: 'Rapporto volume',
    rsi: 'RSI',
    ratingCol: 'Rating',
    presetsLabel: 'Preset rapidi',
    preset: {
      momentum: 'Momentum',
      rsi_oversold: 'RSI ipervenduto',
      breakout_52w: 'Breakout 52 sett.',
      high_volume: 'Volume elevato',
    },
    gainers: 'In rialzo',
    losers: 'In ribasso',
    minVolRatio: 'Rapporto volume minimo',
    ratingLabelFilter: 'Etichetta rating',
    minScore: 'Punteggio minimo',
    rsiThreshold: 'Soglia RSI',
    oversold: 'Ipervenduto',
    overbought: 'Ipercomprato',
    breakoutThreshold: 'Prossimit\u00e0 breakout',
    savePresetPlaceholder: 'Nome preset\u2026',
    savePreset: 'Salva preset',
    savedPresets: 'Preset salvati',
    deletePreset: 'Elimina {{name}}',
    universeDefault: 'Universo predefinito',
    universeWatchlist: 'La mia watchlist',
    universeTopCrypto: 'Top {{n}} crypto',
    universeCatalog: 'Catalogo azionario (US+EU)',
    watchlistPlaceholder: 'Aggiungi simbolo\u2026',
    addSymbol: 'Aggiungi',
    expertRequired: 'Lo Screener di mercato \u00e8 una funzione Expert. Passa a un account Expert per sbloccarla.',
    selectRow: 'Seleziona {{symbol}}',
    selectHint: 'Seleziona fino a {{max}} righe per confrontare, o clicca una riga per vederne l\u2019analisi.',
    compareSelected: 'Confronta selezionati ({{count}})',
    viewDetail: 'Vedi analisi',
  },
  sentiment: {
    title: 'Sentiment & notizie',
    fetch: 'Carica',
    loading: 'Caricamento\u2026',
    sentimentAnalysis: 'Analisi del sentiment',
    score: 'Punteggio',
    articles: 'Articoli',
    perSourceBreakdown: 'Dettaglio per fonte',
    source: 'Fonte',
    label: 'Etichetta',
    priceSentimentDivergence: 'Divergenza prezzo/sentiment',
    priceChange: 'Variazione prezzo',
    sentimentScore: 'Punteggio sentiment',
    newsHeadlines: 'Titoli di notizie',
    selectSymbolHint: 'Seleziona un simbolo e clicca Carica.',
    equitySymbolPlaceholder: 'Simbolo es. AAPL',
  },
  importPage: {
    title: 'Importa',
    subtitle: 'Carica un export CSV o Excel. L’AI associa le colonne; modifichi e confermi prima di importare.',
    step1: '1 Carica',
    step2: '2 Modifica e conferma',
    step3: '3 Fatto',
  },
  settingsPage: {
    accountsTitle: 'Conti di investimento',
    accountsDescription: 'Collega Binance, DeGiro o un indirizzo wallet pubblico per sincronizzare il portafoglio.',
  },
  manualAssets: {
    title: 'Asset Manuali',
    subtitle: 'Attivi che tieni traccia a mano, come immobili, liquidità o investimenti privati.',
  },
  importLog: {
    title: 'Registro import',
    subtitle: 'Cronologia di ogni file CSV/Excel importato in Yiriba.',
    filename: 'File',
    destination: 'Destinazione',
    rows: 'Righe',
    imported: 'Importate',
    skipped: 'Saltate',
    errors: 'Errori',
    date: 'Data',
    empty: 'Nessun import ancora.',
    error: 'Impossibile caricare il registro import.',
    loading: 'Caricamento\u2026',
  },
  explorer: {
    etfTitle: 'Esplora ETF',
    etfDescription: 'Sfoglia il catalogo ETF o visualizza le tue posizioni',
    cryptoTitle: 'Esplora Crypto',
    cryptoDescription: 'Sfoglia il catalogo crypto o visualizza le tue posizioni',
    catalog: 'Catalogo',
    myEtf: 'I miei ETF',
    browse: 'Sfoglia',
    myCrypto: 'Le mie crypto',
    search: 'Cerca',
    etfSearchPlaceholder: 'Nome o ISIN',
    cryptoSearchPlaceholder: 'Nome o simbolo',
    assetClass: 'Classe di attivo',
    domicile: 'Domicilio',
    category: 'Categoria',
    ranked: 'Classificato',
    all: 'Tutti',
    top: 'Top {{n}}',
    peaEligibleOnly: 'Solo idonei PEA',
    isin: 'ISIN',
    name: 'Nome',
    ter: 'TER',
    noEtfMatch: 'Nessun ETF corrisponde a questi filtri.',
    noCryptoMatch: 'Nessuna criptovaluta corrisponde a questi filtri.',
    previous: 'Precedente',
    next: 'Successivo',
    etfCatalogError: 'Impossibile caricare il catalogo ETF. Riprova tra poco.',
    etfPositionsError: 'Impossibile caricare le posizioni ETF. Riprova tra poco.',
    cryptoCatalogError: 'Impossibile caricare il catalogo crypto. Riprova tra poco.',
    cryptoPositionsError: 'Impossibile caricare le posizioni crypto. Riprova tra poco.',
    qty: 'Qt\u00e0',
    price: 'Prezzo',
    value: 'Valore',
    totalReturn: 'Rendimento totale',
    noEtfPositions: 'Nessuna posizione ETF trovata.',
    noCryptoPositions: 'Nessuna posizione crypto trovata.',
    rank: 'Rank',
    symbol: 'Simbolo',
    marketCap: 'Capitalizzazione',
    ath: 'ATH',
    atl: 'ATL',
    refresh: 'Aggiorna prezzi',
    refreshing: 'Aggiornamento\u2026',
    refreshSuccess: 'Prezzi aggiornati.',
    refreshError: 'Aggiornamento non riuscito \u2014 riprova pi\u00f9 tardi.',
  },
  home: {
    title: 'Yiriba',
    subtitle: 'Tracker, analizzatore e simulatore personale di portafoglio ETF e crypto.',
    portfolioCard: 'Portafoglio',
    portfolioDesc: 'P&L aggregato su DeGiro, Binance e Boursorama.',
    etfCard: 'Esplora ETF',
    etfDesc: 'Sfoglia e filtra il catalogo ETF con badge PEA.',
    cryptoCard: 'Esplora Crypto',
    cryptoDesc: 'Prezzi live, indicatori e le tue posizioni Binance.',
    analysisCard: 'Analisi di mercato',
    analysisDesc: 'Tecnici multi-timeframe, rating e sentiment.',
    taxCard: 'Fiscale',
    taxDesc: 'Stime indicative delle imposte sulle plusvalenze IT e FR.',
    exportCard: 'Esporta',
    exportDesc: 'Scarica portafoglio e dati fiscali in Excel o CSV.',
    open: 'Apri',
  },
  tax: {
    title: 'Fiscale',
    staticDisclaimer: 'Stima indicativa generata automaticamente. Verificare sempre con un commercialista / expert-comptable. Nessuna garanzia di correttezza o aggiornamento normativo.',
    italyTab: 'Italia',
    franceTab: 'Francia',
    gainsTab: 'Plusvalenze realizzate',
    methodFifo: 'FIFO (primo entrato, primo uscito)',
    methodAverage: 'Costo medio',
    acquired: 'Acquisto',
    sold: 'Vendita',
    proceeds: 'Ricavo',
    costBasis: 'Costo fiscale',
    daysHeld: 'Giorni di detenzione',
    exportCsv: 'Scarica CSV',
    pooled: 'Aggregato',
    noRealized: 'Nessuna vendita trovata per quest\'anno.',
    unmatchedWarning: 'Alcune vendite superano gli acquisti registrati; l\'eccedenza usa un costo pari a 0.',
    year: 'Anno',
    method: 'Metodo di abbinamento',
    source: 'Fonte',
    allSources: 'Tutte (DeGiro + Binance)',
    degiro: 'DeGiro',
    binance: 'Binance',
    includeManualHoldings: 'Includi posizioni aggiunte manualmente',
    peaOpenDate: 'Data apertura PEA',
    fetch: 'Calcola',
    fetching: 'Calcolo in corso\u2026',
    etfGains: 'Plusvalenze ETF',
    cryptoGains: 'Plusvalenze crypto',
    netGain: 'Guadagno netto',
    taxDue: 'Imposta dovuta',
    grossGains: 'Plusvalenze lorde',
    grossLosses: 'Minusvalenze lorde',
    ivafe: 'IVAFE (0,2% attivit\u00e0 estere)',
    foreignValue: 'Valore attivit\u00e0 estere',
    quadroRw: 'Quadro RW',
    requiresRw: 'Quadro RW richiesto',
    yes: 'S\u00ec',
    no: 'No',
    lossCarryforward: 'Riporto minusvalenze',
    offsetApplied: 'Compensazione applicata',
    remainingGain: 'Guadagno residuo tassabile',
    maxYears: 'Finestra di riporto: {{years}} anni',
    trades: 'Operazioni',
    symbol: 'Simbolo',
    quantity: 'Quantit\u00e0',
    sellPrice: 'Prezzo vendita',
    avgCost: 'Costo medio',
    gain: 'Guadagno/Perdita',
    date: 'Data',
    noTrades: 'Nessuna operazione tassabile trovata per questo anno.',
    peaStatus: 'Stato PEA',
    mature: 'Maturo (\u22655a) \u2014 solo PS',
    notMature: 'Non maturo (<5a) \u2014 PFU completo',
    holdingYears: '{{years}} anni di possesso',
    psTax: 'Contributi sociali (PS 17,2%)',
    irTax: 'Imposta sul reddito (IR)',
    cto: 'CTO (PFU 30%)',
    pea: 'PEA',
    form2042: 'Campi modulo 2042',
    form2074Title: 'Modulo 2074',
    form2074Required: 'Richiesto \u2014 {{reason}}',
    form2074NotRequired: 'Non richiesto',
    selectYearHint: 'Seleziona un anno e clicca Calcola.',
    error: 'Impossibile calcolare la stima fiscale.',
  },
  export: {
    title: 'Esporta',
    excelReportTitle: 'Report Excel completo',
    excelReportDesc: 'Cartella multi-foglio: riepilogo, posizioni per broker, storico transazioni, performance e stime fiscali (IT/FR).',
    year: 'Anno',
    source: 'Broker',
    allSources: 'Tutti i broker',
    degiro: 'DeGiro',
    binance: 'Binance',
    boursorama: 'Boursorama',
    downloadExcel: 'Scarica report Excel',
    downloading: 'Download in corso\u2026',
    positionsTitle: 'Posizioni di portafoglio',
    positionsDesc: 'Solo posizioni correnti, elenco piatto \u2014 senza fogli fiscali.',
    format: 'Formato',
    downloadPositions: 'Scarica posizioni',
    downloadSuccess: 'Download avviato.',
    downloadError: 'Download non riuscito.',
  },
  comparator: {
    title: 'Comparatore',
    expertRequired: 'Il Comparatore \u00e8 una funzione Expert. Passa a un account Expert per sbloccarlo.',
    subtitle: 'Confronta fino a 5 asset fianco a fianco',
    selectAssets: 'Seleziona asset',
    addAsset: 'Aggiungi asset',
    maxAssetsReached: 'Massimo 5 asset',
    minAssetsRequired: 'Seleziona almeno 2 asset',
    performanceChart: 'Performance normalizzata (base 100)',
    statsTable: 'Statistiche',
    correlationMatrix: 'Matrice di correlazione',
    overlapMatrix: 'Overlap posizioni %',
    cagr: 'CAGR',
    volatility: 'Volatilità',
    sharpe: 'Sharpe',
    maxDrawdown: 'Max Drawdown',
    loading: 'Confronto asset in corso...',
    error: 'Impossibile caricare il confronto',
  },
  simulations: {
    title: 'Simulazioni',
    expertRequired: 'Le Simulazioni sono una funzione Expert. Passa a un account Expert per sbloccarle.',
    subtitle: 'Prova come un acquisto, un piano o uno scenario di mercato cambierebbe il tuo portafoglio.',
    tabImpact: 'Analisi impatto',
    tabMonteCarlo: 'Monte Carlo',
    tabPrePurchase: 'Statistiche pre-acquisto',
    impactAsset: 'Asset (ISIN o simbolo)',
    impactAmount: 'Importo (EUR)',
    runImpact: 'Simula acquisto',
    deltaSharpe: 'Δ Sharpe',
    deltaVolatility: 'Δ Volatilità',
    newWeight: 'Nuovo peso %',
    correlation: 'Correlazione',
    mcInitial: 'Importo iniziale (EUR)',
    mcMonthly: 'Versamento mensile (EUR)',
    mcYears: 'Anni',
    mcReturn: 'Rendimento annuo atteso',
    mcVolatility: 'Volatilità annua attesa',
    runMonteCarlo: 'Avvia simulazione',
    probDouble: 'Probabilità di raddoppio',
    prePurchaseAsset: 'Asset (ISIN o simbolo)',
    runPrePurchase: 'Analizza',
    overlapPercentage: 'Overlap %',
    valueAtRisk: 'VaR 95%',
    loading: 'Simulazione in corso...',
    error: 'Impossibile completare la simulazione. Riprova.',
  },
};
export const tsumiFr = {
  navGroups: { portfolio: 'Portefeuille', markets: 'Marchés', tools: 'Outils', reports: 'Rapports' },
  accountMenu: {
    label: 'Menu du compte',
    profile: 'Profil',
    dataStays: 'Vos données sont à vous — IA cloud uniquement si vous le choisissez',
    appearance: 'Apparence',
    language: 'Langue',
    themeDark: 'Sombre',
    themeLight: 'Clair',
    themeSystem: 'Système',
  },
  jeli: {
    emptyTitle: 'Demandez à Jeli pour vos investissements',
    emptyBody: 'Jeli peut expliquer vos positions, les marchés et les simulations. Il voit la page où vous êtes.',
    inputLabel: 'Message pour Jeli',
    placeholder: 'Posez une question…',
    send: 'Envoyer',
    newChat: 'Nouvelle conversation',
  },
  switcher: { kelen: 'Patrimoine, objectifs, simulation', yiriba: 'ETF, crypto, marchés', naforo: 'Comptes bancaires et budgets' },
  yiribaShell: { dataSources: 'Sources de données et licences' },
  nav: {
    lab: 'Jeli Lab',
    asset: 'Actif',
    home: 'Accueil',
    portfolio: 'Portefeuille',
    etf: 'Explorateur ETF',
    crypto: 'Explorateur Crypto',
    analysis: 'Analyse de march\u00e9',
    backtest: 'Backtesting',
    screener: 'Screener',
    sentiment: 'Sentiment',
    manualAssets: 'Actifs Manuels',
    import: 'Import',
    importLog: 'Journal d\u2019import',
    comparator: 'Comparateur',
    simulations: 'Simulations',
    tax: 'Fiscalit\u00e9',
    export: 'Export',
    settings: 'Param\u00e8tres',
    help: 'Aide',
    legal: 'Mentions l\u00e9gales',
    security: 'S\u00e9curit\u00e9',
    about: '\u00c0 propos',
  },
  portfolio: {
    title: 'Portefeuille',
    totalValue: 'Valeur totale',
    pnl: 'Plus/moins-value',
    positions: 'Positions',
    empty: 'Aucune position \u2014 ajoutez votre premier actif.',
    subtitle: 'Positions ETF et crypto, valorisées en euros.',
    totalPortfolio: 'Portefeuille total',
    totalReturn: 'rendement total',
    etf: 'ETF',
    crypto: 'Crypto',
    capitalInvested: 'Capital investi',
    growth: 'Croissance',
    valueOverTime: 'Valeur dans le temps',
    noHistory: 'Aucun historique disponible.',
    valueLabel: 'Valeur',
    allocation: 'Répartition par classe d’actifs',
    colAsset: 'Actif',
    colValue: 'Valeur',
    colReturn: 'Rendement total',
    colType: 'Type',
    offline: 'Backend Yiriba hors ligne — affichage des données en cache si disponibles.',
    positionsError: 'Impossible de charger vos positions. Le total ci-dessus peut être incomplet — réessayez dans un instant.',
    loadError: 'Impossible de charger le portefeuille. Réessayez dans un instant.',
  },
  common: {
    loading: 'Chargement\u2026',
    error: 'Une erreur s\u2019est produite.',
    retry: 'R\u00e9essayer',
    noData: 'Aucune donn\u00e9e disponible.',
    disclaimer: '\u00c0 titre informatif uniquement \u2014 pas un conseil financier.',
    equityDataLicenseNote: 'Les donn\u00e9es de march\u00e9 actions sont fournies via une licence gratuite \u00e0 usage personnel/non commercial. Une licence commerciale payante sera n\u00e9cessaire avant tout lancement commercial de l\u2019application.',
  },
  notFound: {
    title: 'Page introuvable',
    body: 'La page que vous cherchez n\u2019existe pas ou a \u00e9t\u00e9 d\u00e9plac\u00e9e.',
    home: 'Retour au Portefeuille',
  },
  a11y: {
    assetClass: 'Classe d\u2019actifs',
    screenType: 'Type de filtre',
    direction: 'Direction',
    rsiCondition: 'Condition RSI',
    universe: 'Univers',
    symbol: 'Symbole',
    etfSymbol: 'Symbole ETF',
    equitySymbol: 'Symbole action',
    addSymbol: 'Ajouter un symbole',
    assetNotFoundTitle: 'Actif introuvable',
    assetNotFoundBody: 'Aucun actif ne correspond \u00e0 cet identifiant. V\u00e9rifiez le symbole et r\u00e9essayez.',
    askJeli: 'Demander \u00e0 Jeli',
  },
  markets: {
    title: 'Analyse de march\u00e9',
    crypto: 'Crypto',
    equity: 'Actions',
    etf: 'ETF',
    bond: 'Obligations',
    addSymbolPlaceholder: 'Ajouter un symbole du catalogue\u2026',
    equitySymbolPlaceholder: 'Symbole ex. AAPL',
    add: 'Ajouter',
    analyze: 'Analyser',
    analyzing: 'Analyse en cours\u2026',
    compare: 'Comparer',
    selectHint: 'S\u00e9lectionnez 1 symbole \u00e0 analyser, ou 2\u2013{{max}} pour comparer c\u00f4te \u00e0 c\u00f4te.',
    technicalIndicators: 'Indicateurs techniques',
    multiAssetCompare: 'Comparaison multi-actifs',
    emptyAnalyze: 'Ajoutez un symbole et cliquez sur Analyser.',
    correlation: 'Corr\u00e9lation',
    compareHint: 'S\u00e9lectionnez 2\u2013{{max}} symboles et cliquez sur Comparer pour voir la corr\u00e9lation par paire.',
    rating: 'Notation',
    removeSymbol: 'Retirer {{symbol}}',
  },
  backtest: {
    title: 'Backtesting',
    expertRequired: 'Le backtesting est une fonctionnalit\u00e9 Expert. Passez \u00e0 un compte Expert pour le d\u00e9bloquer.',
    run: 'Lancer le backtest',
    running: 'Ex\u00e9cution\u2026',
    strategyBacktest: 'Backtest de strat\u00e9gie',
    totalReturn: 'Rendement total',
    vsBuyHold: 'vs Achat & Conservation',
    sharpe: 'Sharpe',
    maxDrawdown: 'Max Drawdown',
    winRate: 'Taux de r\u00e9ussite',
    trades: 'Op\u00e9rations',
    selectStrategyHint: 'S\u00e9lectionnez une strat\u00e9gie et lancez le backtest.',
    compareStrategiesTitle: 'Comparer les strat\u00e9gies & Walk-Forward',
    compareStrategies: 'Comparer les strat\u00e9gies',
    runCompare: 'Lancer la comparaison',
    comparing: 'Comparaison en cours\u2026',
    strategy: 'Strat\u00e9gie',
    maxDD: 'Max DD',
    best: 'Meilleure',
    compareHint: 'S\u00e9lectionnez au moins 2 strat\u00e9gies ci-dessus, puis lancez la comparaison pour classer par Sharpe.',
    walkForward: 'Analyse Walk-Forward',
    runWalkForward: 'Lancer Walk-Forward',
    insufficientHistory: 'Historique insuffisant pour un split walk-forward sur ce symbole/p\u00e9riode. Essayez une p\u00e9riode plus longue ou un autre symbole.',
    inSampleReturn: 'Rendement in-sample',
    inSampleSharpe: 'Sharpe in-sample',
    oosReturn: 'Rendement OOS',
    oosSharpe: 'Sharpe OOS',
    walkForwardHint: 'D\u00e9tection du surapprentissage via split in-sample / out-of-sample.',
    equitySymbolPlaceholder: 'Symbole ex. AAPL',
  },
  screener: {
    title: 'Screener de march\u00e9',
    subtitle: 'Trouvez des ETF, actions et cryptos selon vos critères. Les résultats sont informatifs, pas des conseils.',
    topN: 'Nombre de cryptos',
    errorTitle: 'L\u2019analyse n\u2019a pas abouti',
    errorHint: 'Vérifiez votre connexion et réessayez.',
    locked: {
      description: 'Scannez le marché : plus fortes variations, volumes inhabituels, extrêmes RSI et plus hauts sur 52 semaines.',
      benefit1: 'Préréglages en un clic pour les stratégies courantes',
      benefit2: 'Votre liste de suivi ou les principales cryptos comme univers',
      benefit3: 'Envoyez n\u2019importe quel résultat vers l\u2019Analyse de marché',
    },
    empty: {
      title: 'Aucun résultat pour ces filtres',
      message: 'Essayez une plage plus large, un autre filtre ou un autre univers.',
      reset: 'Réinitialiser les filtres',
    },
    scan: 'Scanner',
    scanning: 'Analyse en cours\u2026',
    topMovers: 'Plus fortes variations',
    volumeScanner: 'Scanner de volume',
    ratingFilter: 'Filtre de notation',
    scannedSymbols: '{{count}} symboles analys\u00e9s \u00b7 {{results}} r\u00e9sultats',
    symbol: 'Symbole',
    price: 'Prix',
    change: 'Variation',
    volRatio: 'Ratio de volume',
    rsi: 'RSI',
    ratingCol: 'Notation',
    presetsLabel: 'Pr\u00e9r\u00e9glages rapides',
    preset: {
      momentum: 'Momentum',
      rsi_oversold: 'RSI survendu',
      breakout_52w: 'Breakout 52 sem.',
      high_volume: 'Volume \u00e9lev\u00e9',
    },
    gainers: 'Hausses',
    losers: 'Baisses',
    minVolRatio: 'Ratio de volume min.',
    ratingLabelFilter: '\u00c9tiquette de notation',
    minScore: 'Score minimum',
    rsiThreshold: 'Seuil RSI',
    oversold: 'Survendu',
    overbought: 'Surachet\u00e9',
    breakoutThreshold: 'Proximit\u00e9 du breakout',
    savePresetPlaceholder: 'Nom du pr\u00e9r\u00e9glage\u2026',
    savePreset: 'Enregistrer le pr\u00e9r\u00e9glage',
    savedPresets: 'Pr\u00e9r\u00e9glages enregistr\u00e9s',
    deletePreset: 'Supprimer {{name}}',
    universeDefault: 'Univers par d\u00e9faut',
    universeWatchlist: 'Ma liste de suivi',
    universeTopCrypto: 'Top {{n}} crypto',
    universeCatalog: 'Catalogue actions (US+UE)',
    watchlistPlaceholder: 'Ajouter un symbole\u2026',
    addSymbol: 'Ajouter',
    expertRequired: 'Le Screener de march\u00e9 est une fonctionnalit\u00e9 Expert. Passez \u00e0 un compte Expert pour la d\u00e9bloquer.',
    selectRow: 'S\u00e9lectionner {{symbol}}',
    selectHint: 'S\u00e9lectionnez jusqu\u2019\u00e0 {{max}} lignes \u00e0 comparer, ou cliquez sur une ligne pour voir son analyse.',
    compareSelected: 'Comparer la s\u00e9lection ({{count}})',
    viewDetail: 'Voir l\u2019analyse',
  },
  sentiment: {
    title: 'Sentiment & actualit\u00e9s',
    fetch: 'Charger',
    loading: 'Chargement\u2026',
    sentimentAnalysis: 'Analyse du sentiment',
    score: 'Score',
    articles: 'Articles',
    perSourceBreakdown: 'D\u00e9tail par source',
    source: 'Source',
    label: '\u00c9tiquette',
    priceSentimentDivergence: 'Divergence prix/sentiment',
    priceChange: 'Variation de prix',
    sentimentScore: 'Score de sentiment',
    newsHeadlines: 'Titres d\u2019actualit\u00e9',
    selectSymbolHint: 'S\u00e9lectionnez un symbole et cliquez sur Charger.',
    equitySymbolPlaceholder: 'Symbole ex. AAPL',
  },
  importPage: {
    title: 'Importer',
    subtitle: 'Téléversez un export CSV ou Excel. L’IA associe les colonnes ; vous modifiez et confirmez avant tout import.',
    step1: '1 Téléverser',
    step2: '2 Modifier et confirmer',
    step3: '3 Terminé',
  },
  settingsPage: {
    accountsTitle: 'Comptes d’investissement',
    accountsDescription: 'Connectez Binance, DeGiro ou une adresse de portefeuille publique pour synchroniser votre portefeuille.',
  },
  manualAssets: {
    title: 'Actifs Manuels',
    subtitle: 'Actifs que vous suivez à la main, comme l’immobilier, la trésorerie ou des placements privés.',
  },
  importLog: {
    title: 'Journal d\u2019import',
    subtitle: 'Historique de chaque fichier CSV/Excel import\u00e9 dans Yiriba.',
    filename: 'Fichier',
    destination: 'Destination',
    rows: 'Lignes',
    imported: 'Import\u00e9es',
    skipped: 'Ignor\u00e9es',
    errors: 'Erreurs',
    date: 'Date',
    empty: 'Aucun import pour le moment.',
    error: 'Impossible de charger le journal d\u2019import.',
    loading: 'Chargement\u2026',
  },
  explorer: {
    etfTitle: 'Explorateur ETF',
    etfDescription: 'Parcourez le catalogue ETF ou consultez vos positions',
    cryptoTitle: 'Explorateur Crypto',
    cryptoDescription: 'Parcourez le catalogue crypto ou consultez vos positions',
    catalog: 'Catalogue',
    myEtf: 'Mes ETF',
    browse: 'Parcourir',
    myCrypto: 'Mes cryptos',
    search: 'Rechercher',
    etfSearchPlaceholder: 'Nom ou ISIN',
    cryptoSearchPlaceholder: 'Nom ou symbole',
    assetClass: 'Classe d\u2019actif',
    domicile: 'Domicile',
    category: 'Cat\u00e9gorie',
    ranked: 'Class\u00e9',
    all: 'Tous',
    top: 'Top {{n}}',
    peaEligibleOnly: '\u00c9ligible PEA uniquement',
    isin: 'ISIN',
    name: 'Nom',
    ter: 'TER',
    noEtfMatch: 'Aucun ETF ne correspond \u00e0 ces filtres.',
    noCryptoMatch: 'Aucune cryptomonnaie ne correspond \u00e0 ces filtres.',
    previous: 'Pr\u00e9c\u00e9dent',
    next: 'Suivant',
    etfCatalogError: 'Impossible de charger le catalogue ETF. R\u00e9essayez dans un instant.',
    etfPositionsError: 'Impossible de charger les positions ETF. R\u00e9essayez dans un instant.',
    cryptoCatalogError: 'Impossible de charger le catalogue crypto. R\u00e9essayez dans un instant.',
    cryptoPositionsError: 'Impossible de charger les positions crypto. R\u00e9essayez dans un instant.',
    qty: 'Qt\u00e9',
    price: 'Prix',
    value: 'Valeur',
    totalReturn: 'Rendement total',
    noEtfPositions: 'Aucune position ETF trouv\u00e9e.',
    noCryptoPositions: 'Aucune position crypto trouv\u00e9e.',
    rank: 'Rang',
    symbol: 'Symbole',
    marketCap: 'Capitalisation',
    ath: 'ATH',
    atl: 'ATL',
    refresh: 'Actualiser les prix',
    refreshing: 'Actualisation\u2026',
    refreshSuccess: 'Prix actualis\u00e9s.',
    refreshError: '\u00c9chec de l\u2019actualisation \u2014 r\u00e9essayez plus tard.',
  },
  home: {
    title: 'Yiriba',
    subtitle: 'Suivi, analyse et simulation personnels de portefeuille ETF et crypto.',
    portfolioCard: 'Portefeuille',
    portfolioDesc: 'P&L agr\u00e9g\u00e9 sur DeGiro, Binance et Boursorama.',
    etfCard: 'Explorateur ETF',
    etfDesc: 'Parcourez et filtrez le catalogue ETF avec badges PEA.',
    cryptoCard: 'Explorateur Crypto',
    cryptoDesc: 'Prix en direct, indicateurs et vos positions Binance.',
    analysisCard: 'Analyse de march\u00e9',
    analysisDesc: 'Techniques multi-temporalit\u00e9s, notation et sentiment.',
    taxCard: 'Fiscalit\u00e9',
    taxDesc: 'Estimations indicatives des plus-values italiennes et fran\u00e7aises.',
    exportCard: 'Export',
    exportDesc: 'T\u00e9l\u00e9chargez portefeuille et donn\u00e9es fiscales en Excel ou CSV.',
    open: 'Ouvrir',
  },
  tax: {
    title: 'Fiscalit\u00e9',
    staticDisclaimer: 'Estimation indicative g\u00e9n\u00e9r\u00e9e automatiquement. Toujours v\u00e9rifier avec un expert-comptable. Aucune garantie d\u2019exactitude ou de mise \u00e0 jour r\u00e9glementaire.',
    italyTab: 'Italie',
    franceTab: 'France',
    gainsTab: 'Plus-values réalisées',
    methodFifo: 'FIFO (premier entré, premier sorti)',
    methodAverage: 'Coût moyen',
    acquired: 'Acquisition',
    sold: 'Vente',
    proceeds: 'Produit',
    costBasis: 'Prix de revient',
    daysHeld: 'Jours de détention',
    exportCsv: 'Télécharger le CSV',
    pooled: 'Regroupé',
    noRealized: 'Aucune vente trouvée pour cette année.',
    unmatchedWarning: 'Certaines ventes dépassent les achats enregistrés ; l’excédent utilise un prix de revient de 0.',
    year: 'Ann\u00e9e',
    method: 'M\u00e9thode d\u2019appariement',
    source: 'Source',
    allSources: 'Toutes (DeGiro + Binance)',
    degiro: 'DeGiro',
    binance: 'Binance',
    includeManualHoldings: 'Inclure les positions ajout\u00e9es manuellement',
    peaOpenDate: 'Date d\u2019ouverture du PEA',
    fetch: 'Calculer',
    fetching: 'Calcul en cours\u2026',
    etfGains: 'Plus-values ETF',
    cryptoGains: 'Plus-values crypto',
    netGain: 'Gain net',
    taxDue: 'Imp\u00f4t d\u00fb',
    grossGains: 'Plus-values brutes',
    grossLosses: 'Moins-values brutes',
    ivafe: 'IVAFE (0,2% actifs \u00e9trangers)',
    foreignValue: 'Valeur des actifs \u00e9trangers',
    quadroRw: 'Quadro RW',
    requiresRw: 'Quadro RW requis',
    yes: 'Oui',
    no: 'Non',
    lossCarryforward: 'Report des moins-values',
    offsetApplied: 'Compensation appliqu\u00e9e',
    remainingGain: 'Gain imposable restant',
    maxYears: 'Fen\u00eatre de report\u00a0: {{years}} ans',
    trades: 'Transactions',
    symbol: 'Symbole',
    quantity: 'Quantit\u00e9',
    sellPrice: 'Prix de vente',
    avgCost: 'Co\u00fbt moyen',
    gain: 'Gain/Perte',
    date: 'Date',
    noTrades: 'Aucune transaction imposable trouv\u00e9e pour cette ann\u00e9e.',
    peaStatus: 'Statut du PEA',
    mature: 'Mature (\u22655a) \u2014 PS uniquement',
    notMature: 'Non mature (<5a) \u2014 PFU complet',
    holdingYears: '{{years}} ans de d\u00e9tention',
    psTax: 'Pr\u00e9l\u00e8vements sociaux (PS 17,2%)',
    irTax: 'Imp\u00f4t sur le revenu (IR)',
    cto: 'CTO (PFU 30%)',
    pea: 'PEA',
    form2042: 'Champs formulaire 2042',
    form2074Title: 'Formulaire 2074',
    form2074Required: 'Requis \u2014 {{reason}}',
    form2074NotRequired: 'Non requis',
    selectYearHint: 'S\u00e9lectionnez une ann\u00e9e et cliquez sur Calculer.',
    error: 'Impossible de calculer l\u2019estimation fiscale.',
  },
  export: {
    title: 'Export',
    excelReportTitle: 'Rapport Excel complet',
    excelReportDesc: 'Classeur multi-feuilles\u00a0: r\u00e9sum\u00e9, positions par courtier, historique des transactions, performance et estimations fiscales (IT/FR).',
    year: 'Ann\u00e9e',
    source: 'Courtier',
    allSources: 'Tous les courtiers',
    degiro: 'DeGiro',
    binance: 'Binance',
    boursorama: 'Boursorama',
    downloadExcel: 'T\u00e9l\u00e9charger le rapport Excel',
    downloading: 'T\u00e9l\u00e9chargement\u2026',
    positionsTitle: 'Positions du portefeuille',
    positionsDesc: 'Positions actuelles uniquement, liste plate \u2014 sans feuilles fiscales.',
    format: 'Format',
    downloadPositions: 'T\u00e9l\u00e9charger les positions',
    downloadSuccess: 'T\u00e9l\u00e9chargement d\u00e9marr\u00e9.',
    downloadError: '\u00c9chec du t\u00e9l\u00e9chargement.',
  },
  comparator: {
    title: 'Comparateur',
    expertRequired: 'Le Comparateur est une fonctionnalit\u00e9 Expert. Passez \u00e0 un compte Expert pour le d\u00e9bloquer.',
    subtitle: 'Comparez jusqu\u2019à 5 actifs côte à côte',
    selectAssets: 'Sélectionner les actifs',
    addAsset: 'Ajouter un actif',
    maxAssetsReached: 'Maximum 5 actifs',
    minAssetsRequired: 'Sélectionnez au moins 2 actifs',
    performanceChart: 'Performance normalisée (base 100)',
    statsTable: 'Statistiques',
    correlationMatrix: 'Matrice de corrélation',
    overlapMatrix: 'Chevauchement des positions %',
    cagr: 'TCAC',
    volatility: 'Volatilité',
    sharpe: 'Sharpe',
    maxDrawdown: 'Drawdown maximal',
    loading: 'Comparaison des actifs...',
    error: 'Impossible de charger la comparaison',
  },
  simulations: {
    title: 'Simulations',
    expertRequired: 'Les Simulations sont une fonctionnalit\u00e9 Expert. Passez \u00e0 un compte Expert pour les d\u00e9bloquer.',
    subtitle: 'Testez comment un achat, un plan ou un scénario de marché changerait votre portefeuille.',
    tabImpact: 'Analyse d\u2019impact',
    tabMonteCarlo: 'Monte Carlo',
    tabPrePurchase: 'Stats pré-achat',
    impactAsset: 'Actif (ISIN ou symbole)',
    impactAmount: 'Montant (EUR)',
    runImpact: 'Simuler l\u2019achat',
    deltaSharpe: 'Δ Sharpe',
    deltaVolatility: 'Δ Volatilité',
    newWeight: 'Nouvelle pondération %',
    correlation: 'Corrélation',
    mcInitial: 'Montant initial (EUR)',
    mcMonthly: 'Versement mensuel (EUR)',
    mcYears: 'Années',
    mcReturn: 'Rendement annuel attendu',
    mcVolatility: 'Volatilité annuelle attendue',
    runMonteCarlo: 'Lancer la simulation',
    probDouble: 'Probabilité de doubler',
    prePurchaseAsset: 'Actif (ISIN ou symbole)',
    runPrePurchase: 'Analyser',
    overlapPercentage: 'Chevauchement %',
    valueAtRisk: 'VaR 95%',
    loading: 'Simulation en cours...',
    error: 'La simulation n\u2019a pas pu aboutir. Veuillez réessayer.',
  },
};
